import { Card, Col, Form, Radio, Row, Spin, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useGetCredential } from "../hooks/use-get-credential";
import Input from "antd/es/input";
import VuiFormActionButton from "../../../components/form-action-button";
import { useCallback, useEffect, useState } from "react";
import VuiPageTitle from "../../../components/page-title";
import { useFormCredential } from "../hooks/use-form-credential";
import { CredentialFormData, CredentialPayloadType } from "./interface";
import { openNotification } from "../../../utils/helpers";
import VuiAccessible from "../../../components/accessible";
import VuiModalConfirmation from "../../../components/modal-confirmation";
import { toFileList } from "../../../components/upload-image/function";
import VuiUploadImage from "../../../components/upload-image";

const { TextArea } = Input;
const { Text } = Typography;

const CredentialFormModule = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const title = id
    ? t("pages.credential.detail.title")
    : t("pages.credential.add.title");
  const { loadCredentialData, isOnFetchingCredentialData, credentialData } =
    useGetCredential();
  const { submitLoading, onSubmit, onDelete, deleteLoading } =
    useFormCredential();
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const [form] = Form.useForm<CredentialFormData>();

  const onFinish = useCallback(
    async (data: CredentialFormData) => {
      const normalizeData: CredentialPayloadType = {
        ...data,
        attachment_id:
          data.attachment_id && data.attachment_id.length
            ? (data.attachment_id[0].id as number)
            : undefined,
      };

      await onSubmit(normalizeData, id);
    },
    [id, onSubmit]
  );

  const handleCloseModalDelete = useCallback(() => {
    setShowDeleteModal(false);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (id) {
      await onDelete(id).then(() => {
        setShowDeleteModal(false);
        openNotification(
          "success",
          t("notification.success.deleteItem", {
            item: t("common.text.credential"),
          })
        );
        navigate(`/other-list/credential`);
      });
    }
  }, [id, navigate, onDelete, t]);

  useEffect(() => {
    if (id) {
      (async () => {
        await loadCredentialData(id, { with: ["attachment"] });
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (id && credentialData) {
      form.setFieldsValue({
        ...credentialData,
        attachment_id: toFileList(credentialData.attachment),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credentialData]);

  return (
    <>
      <VuiPageTitle title={title} onBackLink="/other-list/credential" />

      <Spin size="large" spinning={isOnFetchingCredentialData}>
        <Form
          form={form}
          layout={"vertical"}
          initialValues={{
            is_active: true,
          }}
          onFinish={onFinish}
        >
          <Row gutter={[16, 16]}>
            <Col md={18} xs={24}>
              <Card title={t("common.text.information")}>
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="name"
                      label={t("common.form.credentialName.label")}
                      rules={[
                        {
                          required: true,
                          message: t("validation.required", {
                            item: t("common.form.credentialName.label"),
                          }),
                        },
                      ]}
                    >
                      <Input
                        size="large"
                        placeholder={t(
                          "common.form.credentialName.placeholder"
                        )}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={24}>
                    <Form.Item
                      name="attachment_id"
                      rules={[
                        {
                          required: true,
                          message: t("validation.required", {
                            item: t("common.form.attachment.label"),
                          }),
                        },
                      ]}
                      label={t("common.form.attachment.label")}
                    >
                      <VuiUploadImage />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Col>

            <Col md={6} xs={24}>
              <Card title={t("common.text.status")}>
                <Form.Item name="is_active">
                  <Radio.Group>
                    <Radio value={true}>Active</Radio>
                    <Radio value={false}>Not Active</Radio>
                  </Radio.Group>
                </Form.Item>
              </Card>
            </Col>

            <Col md={24} xs={24}>
              <VuiFormActionButton
                cancelBtnLink="/other-list/credential"
                isLoading={submitLoading}
              />
            </Col>

            {id && (
              <VuiAccessible access="credential.destroy">
                <Col xs={24}>
                  <Text
                    className="cursor-pointer"
                    onClick={() => setShowDeleteModal(true)}
                    title={t("common.text.delete")}
                    type="secondary"
                  >
                    {t("common.text.delete")}
                  </Text>
                </Col>
              </VuiAccessible>
            )}
          </Row>
        </Form>
      </Spin>

      <VuiModalConfirmation
        show={showDeleteModal}
        isLoading={deleteLoading}
        onSubmit={handleConfirmDelete}
        onCancel={handleCloseModalDelete}
      />
    </>
  );
};

export default CredentialFormModule;
